import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const features = [
  {
    icon: <CheckCircleRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Control total de préstamos y pagos',
    description: 'Gestiona todo el ciclo de vida del préstamo desde la originación hasta el pago con calendarios de amortización automatizados.',
    iconBg: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)',
    iconColor: '#ffffff',
    cardHover: 'linear-gradient(135deg, rgba(85, 180, 172, 0.05) 0%, rgba(24, 88, 107, 0.05) 100%)',
  },
  {
    icon: <Inventory2RoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Gestión de inventario',
    description: 'Rastrea vehículos, motocicletas e inventario de bienes en tiempo real con gestión detallada de activos.',
    iconBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    iconColor: '#ffffff',
    cardHover: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  },
  {
    icon: <PeopleRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Cobranzas automatizadas',
    description: 'Optimiza los procesos de cobranza con recordatorios automatizados, seguimiento de pagos y gestión de mora.',
    iconBg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    iconColor: '#ffffff',
    cardHover: 'linear-gradient(135deg, rgba(240, 147, 251, 0.05) 0%, rgba(245, 87, 108, 0.05) 100%)',
  },
  {
    icon: <AssessmentRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Reportes integrales',
    description: 'Genera reportes financieros detallados, análisis e insights para tomar decisiones basadas en datos.',
    iconBg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    iconColor: '#ffffff',
    cardHover: 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%)',
  },
  {
    icon: <AccountBalanceRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Gestión multi-sucursal',
    description: 'Control centralizado en múltiples sucursales con reportes consolidados y operaciones unificadas.',
    iconBg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    iconColor: '#ffffff',
    cardHover: 'linear-gradient(135deg, rgba(48, 207, 208, 0.05) 0%, rgba(51, 8, 103, 0.05) 100%)',
  },
  {
    icon: <CloudQueueRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Plataforma en la nube',
    description: 'Accede a tus datos en cualquier lugar y momento con infraestructura en la nube segura y respaldos automáticos.',
    iconBg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    iconColor: '#18586b',
    cardHover: 'linear-gradient(135deg, rgba(255, 236, 210, 0.1) 0%, rgba(252, 182, 159, 0.1) 100%)',
  },
];

export default function Features() {
  return (
    <Container id="features" maxWidth="lg" sx={{ py: { xs: 8, sm: 12 } }}>
      {/* What is Bankivo Section */}
      <Box
        sx={{
          mb: { xs: 8, sm: 12 },
          animation: `${fadeInUp} 1s ease-out`,
        }}
      >
        <Typography
          component="h2"
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: 'text.primary',
            mb: 4,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          ¿Qué es Bankivo?
        </Typography>
        <Box
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            textAlign: 'left',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.8,
              mb: 2,
            }}
          >
            Bankivo es un software financiero completo diseñado para empresas financieras modernas, cooperativas y negocios de crédito.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.8,
              mb: 2,
            }}
          >
            Construido con una <Box component="span" sx={{ fontWeight: 700 }}>arquitectura multi-empresa y multi-sucursal</Box>, Bankivo escala sin esfuerzo en toda tu organización.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.8,
            }}
          >
            Nuestra <Box component="span" sx={{ fontWeight: 700 }}>plataforma modular</Box> se adapta a tus necesidades específicas — desde financiamiento vehicular y préstamos personales hasta cooperativas y casas de empeño.
          </Typography>
        </Box>
      </Box>

      {/* Everything you need to succeed Section */}
      <Box
        sx={{
          animation: `${fadeInUp} 1s ease-out 0.2s both`,
        }}
      >
        <Typography
          component="h2"
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Todo lo que necesitas para tener éxito
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            fontSize: { xs: '1rem', sm: '1.125rem' },
          }}
        >
          Funcionalidades poderosas diseñadas para modernizar tus operaciones financieras.
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 2,
                  borderColor: 'grey.300',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `${scaleIn} 0.6s ease-out ${0.1 * index}s both`,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: feature.iconBg,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.4s ease',
                  },
                  '&:hover': {
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-8px) scale(1.02)',
                    borderColor: 'transparent',
                    background: feature.cardHover,
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                    '& .icon-container': {
                      transform: 'scale(1.1) rotate(5deg)',
                      animation: `${pulse} 2s ease-in-out infinite`,
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    className="icon-container"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: feature.iconBg,
                      color: feature.iconColor,
                      mb: 2,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      animation: `${float} 3s ease-in-out infinite`,
                      animationDelay: `${index * 0.2}s`,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1.5,
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}
